import React, { useContext, useMemo, useState } from "react";
import { useRouter } from "next/router";
import classnames from "classnames";
import { useTranslation } from "next-i18next";

// Context
import { AppContext } from "@vahak/core/dist/app-context";

// Constant
import { ComponentState } from "@vahak/core/dist/constants/misc";

import { STATE_LIST } from "@vahak/core/dist/constants/StateList";
import { ACTION_BY_LORRY_STATUS, LORRY_STATUS } from "@vahak/core/dist/constants/lorryStatus";
import { LORRY_BODY_TYPE_MAP, getLorryDetailsByType } from "@vahak/core/dist/constants/lorryTypes";
import { LorryScreenEvents } from "@vahak/core/dist/constants/event-names";
import COLORS from "@vahak/core-ui/dist/constants/colors";

// Component
import Col from "@vahak/core/dist/components/grid/col/Col";
import Row from "@vahak/core/dist/components/grid/row/Row";
import Skeleton from "@vahak/core/dist/components/skeleton/Skeleton";
import DotSeparated from "@vahak/core/dist/components/dot-separated/DotSeparated";
import SrcDest from "../../../../load-lorry-details/components/src-dest/SrcDest";
import LorryBadgeController from "../../../../badge/lorry-badge-controller/LorryBadgeController";
import ExpiryPill from "../../../../marketplace/common/expiry-pill/ExpiryPill";
import ShareModal from "../../../../marketplace/common/share-modal/ShareModal";
import ReactivateLoadLorry from "../../reactivate-load-lorry/ReactivateLoadLorry";
import AppImg from "../../../../../components/utils/AppImg";
import Typography from "@vahak/core-ui/dist/components/Typography/Typography";
import Flex from "@vahak/core-ui/dist/layout/Flex";
import IfCanAccess from "../../../../admin/IfCanAccess/IfCanAccess";
import TextLabelAndValue from "../../../../admin/ticket-system/common/TextLabelAndValue/TextLabelAndValue";
import StopPropagation from "../../../../helper/StopPropagation";
import Dropdown, { IDropdownOption } from "@vahak/core-ui/dist/components/Dropdown";
import AssuredLoadStatus from "../../../../assured-load/AssuredLoadStatus";
import AppRedirectPayBtn from "../../../../assured-load/app-redirection/AppRedirectPayBtn";
// Styles
import Styles from "./LorryCard.module.scss";

// Icons
import ArrowIcon from "@vahak/core/dist/icons/AngleLeftGray.svg";
import ShareBlue from "@vahak/core/dist/icons/share_blue.svg";
import CallCircle from "@vahak/core/dist/icons/call-circle.svg";
import DotsMoreIcon from "@vahak/core/dist/standard-icons/options.svg";

// Methods
import { getFormattedTime } from "@vahak/core/dist/methods/getFormattedTime";
import { formatLorryNum } from "@vahak/core/dist/methods/formatLorryNum";

// Types
import LorryOneLineInfo from "../../../../common/lorry-one-line-info/LorryOneLineInfo";
import { getImageNameFromURL } from "../../../../../utils/getImageNameFromURL";
import { generataMaskUrlWithParams } from "../../../../../methods/generataMaskUrlWithParams";
import { LorryListTypes } from "@vahak/core/dist/custom-types/lorry";
import { initialLoadCreationFormValues, lorryCreationFormFieldsTypes } from "../../../../posting/helpers/constants";
import { BID_STATUS } from "@vahak/core/dist/constants/bidStatus";
import LorryActionBar, { getLorryVerificationDocumentUrlFromRcData } from "./LorryActionBar/LorryActionBar";
import { TicketStatus } from "../../../../../types/document";
import { ADMIN_OPERATION } from "../../../../../constants/adminOperation";
import EllipsisText from "../../../../EllipsisText/EllipsisText";
import ProfileVerificationLevel from "../../../../admin/ticket-system/common/ProfileVerificationLevel/ProfileVerificationLevel";
import VerifiedPill from "../../../../marketplace/common/verified-pill/VerifiedPill";
import { useEventTrackerService } from "@vahak/core/dist/_services/hooks/useEventTrackerService";
import { TrackEventHandler } from "../../../../../types/events";
import { AssuredBidStatusList } from "../../../../admin/ticket-system-helper/assuredLoad";
import { AssuredLoadBidMeta } from "../../../../assured-load/helper";
import { BidType } from "../../../../../services/bid/usePostLoadBid";

import { getJoinedStringFromArray } from "@vahak/core-ui/dist/methods/array";
import { removeSubstringWithParenthesis } from "../../../../../methods/removeSubstringWithParenthesis";
import { useCsDashboard } from "@vahak/core/dist/hooks/useCsDashboard";
import { ILorryPostingContextData } from "../../../../../context/load-lorry-posting";
import { getBookingMeta } from "../../../../admin/ticket-system-helper/assured-helper";

// Interface
interface BaseProps {
    state?: ComponentState;
}

interface lorryCardProps {
    data: LorryListTypes;
    onCardClick?: () => void;
    onCardClickTrackingHandler?: () => void;
    state?: ComponentState.NORMAL;
    isLastCard?: boolean;
    handleEditClick: (
        data: lorryCreationFormFieldsTypes,
        id: number,
        category: ILorryPostingContextData["category"]
    ) => void;
    showProfileInfo?: boolean;
    onClickGetContact?: (transporter_id: number) => void;
    onClickViewDocument?: (lorryId: number) => void;
}

interface loadingState extends BaseProps {
    state?: ComponentState.LOADING;
}

type Props = lorryCardProps | loadingState;

const LorryCard = ({ state = ComponentState.NORMAL, ...props }: Props) => {
    const { t } = useTranslation(["common"]);
    const {
        data,
        onCardClick,
        onCardClickTrackingHandler,
        isLastCard,
        handleEditClick,
        showProfileInfo = false,
        onClickGetContact,
        onClickViewDocument
    } = props as lorryCardProps;
    const { isMobile, customerCompanyId, companyId } = useContext(AppContext);
    const { isCsDashboardUser, checkPermission } = useCsDashboard();

    const [activeCard, setActiveCard] = useState(0);
    const [openShareModal, setOpenShareModal] = useState(false);
    const router = useRouter();
    const { sendGAandMoEngageEvent } = useEventTrackerService();

    // Hooks
    // const { openModal, closeModal } = useHandleModalToggle(["shareModal"]);

    const isVerified = !!data?.lorry_detail?.is_verified;
    const cId = Number(customerCompanyId || companyId || 0);
    const allowEditLorryStatus = ACTION_BY_LORRY_STATUS["EDIT_LORRY"].includes(data?.lorry_detail?.status);
    const allowEditPermission = checkPermission(ADMIN_OPERATION.userLoadLorryAction);

    const VerificationStatus = isVerified && (
        <Col className={Styles.verifiedPill}>
            <VerifiedPill />
        </Col>
    );
    const trackEvent: TrackEventHandler = (name, _data) => {
        sendGAandMoEngageEvent({
            name,
            data: {
                source_screen: "my_lorry",
                from_screen: "my_lorry",
                ...generateLorryEventAttribute(data),
                ..._data
            }
        });
    };

    if (state === ComponentState.LOADING) {
        return !isMobile ? (
            <div className={classnames(Styles.lorryCardContainer, Styles.loadingState)} onClick={() => {}}>
                <Row className={Styles.mainRow}>
                    <Col md={2}>
                        <div className={Styles.lorryNumberContainer}>
                            <div className={Styles.lorryIcon}>
                                <Skeleton />
                            </div>
                            <div className={Styles.lorryNumber}>
                                <span>
                                    <Skeleton />
                                </span>
                            </div>
                        </div>
                    </Col>
                    <Col>
                        <div className={Styles.lorryClassificationContainer}>
                            <span>
                                <Skeleton />
                            </span>
                            <span>
                                <Skeleton width={210} />
                            </span>
                        </div>
                    </Col>
                    <Col md={3}>
                        <div className={Styles.srcDestContainer}>
                            <SrcDest state={ComponentState.LOADING} />
                        </div>
                    </Col>
                    <Col md={2}>
                        <div className={Styles.statusContainer}>
                            <Skeleton width={90} height={28} type="pill" />
                            <Skeleton />
                        </div>
                    </Col>
                    <Col>
                        <div className={Styles.timeStamp}>
                            <span>
                                <Skeleton />
                            </span>
                            <span>
                                <Skeleton />
                            </span>
                        </div>
                    </Col>
                </Row>
            </div>
        ) : (
            <div className={classnames(Styles.lorryCardContainer, Styles.loadingState)}>
                <div className={Styles.lorryCardSpaceWrapper}>
                    <Row>
                        <Col>
                            <div className={Styles.lorryClassificationContainer}>
                                <span className={Styles.lorryId}>
                                    <Skeleton />
                                </span>
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col className={Styles.srcDestCol} xs={8}>
                            <div className={Styles.srcDestContainer}>
                                <SrcDest state={ComponentState.LOADING} />
                            </div>
                        </Col>
                        <Col xs={4}>
                            <div className={Styles.icon}>
                                <Skeleton />
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={8}>
                            <span className={Styles.lorryClassification}>
                                <Skeleton />
                            </span>
                        </Col>
                        <Col xs={4}>
                            <div className={Styles.lorryNumber}>
                                <span>
                                    <Skeleton />
                                </span>
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <div className={Styles.footerSectionWrapper}>
                                <div className={Styles.footerSectionContainer}>
                                    <span className={classnames(Styles.bidStatus)}>{<Skeleton />}</span>
                                    <span className={Styles.viewDetailsBtn}>
                                        <Skeleton />
                                    </span>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>
        );
    }

    const lorryClassification = getLorryDetailsByType(data.lorry_detail.type_value);

    const lorry_icon_right = lorryClassification?.iconRight;
    const lorry_icon_left = lorryClassification?.iconLeft;

    let received_bids = 0,
        sent_bids = 0,
        acceptedAtAmount = 0;
    if (data && data?.bids_summary) {
        received_bids = data.bids_summary.received_bids;
        sent_bids = data.bids_summary.sent_bids;
    }

    if (data && data.bid_infos) {
        const { cross_bid, amount } = data.bid_infos.find((bid: any) => bid.status === BID_STATUS.IN_TRANSIT) ?? {};
        acceptedAtAmount = cross_bid || amount || 0;
    }

    const getBidStatus = () => {
        switch (data.lorry_detail.status) {
            case LORRY_STATUS.IN_TRANSIT: {
                return acceptedAtAmount ? `Accepted bid: ₹${acceptedAtAmount}` : "Bid Accepted";
            }
            case LORRY_STATUS.ACTIVE: {
                if (!!data?.bids_summary) {
                    return `${sent_bids + " Sent / " + received_bids + " Received"} `;
                } else {
                    return "Awaiting Bids";
                }
            }
            default: {
                return "";
            }
        }
    };

    const bidStatus = getBidStatus();

    const handleShareLorry = (e: any) => {
        e.stopPropagation();
        setActiveCard(data.lorry_detail.id);
        setOpenShareModal(true);
        trackEvent(LorryScreenEvents["shareonwhatsapp_clicked"], {
            activity: "lorry"
        });
    };

    const handleReactivationSuccess = (val: number) => {
        router.query["id"] = val.toString();
        router.push(router, generataMaskUrlWithParams(router.query), {
            shallow: true
        });
        trackEvent(LorryScreenEvents["reactivated_lorry"], {});
    };

    const isRCDocumentUploaded = !!getLorryVerificationDocumentUrlFromRcData(data?.lorry_detail?.rc_details);
    const isVerificationPending =
        isRCDocumentUploaded && data?.lorry_detail?.rc_details?.status === TicketStatus.PENDING;

    const getRCVerificationComponent = () => {
        const isVerifiedLoad = !data?.lorry_detail || data?.lorry_detail?.status !== LORRY_STATUS.VERIFICATION_FAILED;

        if (isVerifiedLoad) {
            return null;
        }

        const { rc_details = {}, id } = data?.lorry_detail ?? {};

        return isMobile ? (
            <LorryActionBar lorryId={id} rcDetails={rc_details} trackEvent={trackEvent} />
        ) : (
            <Row>
                <div className={classnames(Styles.lorryActions)}>
                    <LorryActionBar lorryId={id} rcDetails={rc_details} trackEvent={trackEvent} />
                </div>
            </Row>
        );
    };

    let renderTapNgoTimeline;

    const paymentPendingAssuredBids = data?.bid_infos?.find((bid) => {
        const isAssuredLoad = bid?.load_detail?.is_tap_n_go || bid?.load_detail?.is_spot_load;
        const bookingMeta = getBookingMeta({ isTapNgo: bid?.load_detail?.is_tap_n_go }, bid);
        return (
            AssuredBidStatusList.includes(bid?.status) ||
            (isAssuredLoad && bid?.status === BID_STATUS.IN_TRANSIT && !bookingMeta?.is_lorry_owner_paid)
        );
    })!;

    if (paymentPendingAssuredBids) {
        const { bidder_info, tenderer_info } = paymentPendingAssuredBids;

        const isBidder = cId === bidder_info?.id;
        const assuredLoadMetaData: AssuredLoadBidMeta = {
            spot_load_info: paymentPendingAssuredBids?.spot_load_info,
            tap_n_go_info: paymentPendingAssuredBids?.tap_n_go_info,
            updated_at_timestamp: paymentPendingAssuredBids?.updated_at_timestamp,
            bid_status: paymentPendingAssuredBids?.status,
            is_tap_n_go: !!paymentPendingAssuredBids?.load_detail?.is_tap_n_go,
            is_spot_load: !!paymentPendingAssuredBids?.load_detail?.is_spot_load,
            loadCid: isBidder ? tenderer_info?.id : bidder_info?.id,
            lorryCid: !isBidder ? tenderer_info?.id : bidder_info?.id,
            bidId: paymentPendingAssuredBids?.id,
            loadId: paymentPendingAssuredBids?.load_detail?.id,
            bookingOrderInfo: {
                bid_type: isBidder ? BidType.LOAD_BID : BidType.LORRY_BID
            }
        };

        const isAssuredLoad = assuredLoadMetaData?.is_spot_load || assuredLoadMetaData?.is_tap_n_go;
        if (isAssuredLoad && companyId) {
            renderTapNgoTimeline = (
                <AssuredLoadStatus
                    meta={assuredLoadMetaData}
                    cId={Number(companyId)}
                    source="lorry"
                    trackEvent={trackEvent}
                />
            );
        }
    }

    const headerDropdownList: IDropdownOption[] = [
        {
            id: "edit-lorry",
            label: "Edit Lorry",
            enable: allowEditLorryStatus && allowEditPermission,
            onClick() {
                handleEditClick(getLorryPreFilledData(data), data.lorry_detail.id, "edit");
            }
        },
        {
            id: "lorry-documents",
            label: "Documents",
            enable: allowEditPermission,
            onClick() {
                onClickViewDocument?.(data?.lorry_detail?.id!);
            }
        }
    ].filter((item) => item.enable);

    const DropdownSlot = useMemo(() => {
        return (
            <>
                {!!headerDropdownList?.length && (
                    <StopPropagation>
                        <Dropdown
                            options={headerDropdownList}
                            toggleNode={<DotsMoreIcon />}
                            externalProps={{
                                isRtl: true
                            }}
                        />
                    </StopPropagation>
                )}
            </>
        );
    }, [headerDropdownList]);

    return (
        <>
            {!isMobile ? (
                // Desktop Card
                <div
                    className={classnames(Styles.lorryCardContainer)}
                    onClick={() => {
                        onCardClick?.();
                        onCardClickTrackingHandler
                            ? onCardClickTrackingHandler?.()
                            : trackEvent(LorryScreenEvents["lorry_card_clicked"]);
                    }}
                >
                    {VerificationStatus}
                    <Row className={Styles.mainRow}>
                        <div className={Styles.lorryIconCol}>
                            <div className={Styles.lorryNumberContainer}>
                                <div className={Styles.lorryIcon}>
                                    {lorry_icon_right ? (
                                        <AppImg
                                            className={Styles.icon}
                                            src={getImageNameFromURL(lorry_icon_right)}
                                            alt=""
                                            width="100%"
                                            height="50px"
                                        />
                                    ) : null}
                                    {data.lorry_detail.status !== LORRY_STATUS.IN_ACTIVE ? (
                                        <div className={Styles.shareIcon} onClick={handleShareLorry}>
                                            <ShareBlue />
                                        </div>
                                    ) : (
                                        <></>
                                    )}
                                </div>
                                <div className={Styles.lorryNumber}>
                                    <span>{formatLorryNum(data.lorry_detail.number)}</span>
                                </div>
                            </div>
                        </div>
                        <div className={Styles.lorryClassificationCol}>
                            <div className={Styles.lorryClassificationContainer}>
                                <div>{lorryClassification?.label}</div>
                                <LorryOneLineInfo
                                    lorryBodyType={data.lorry_detail.body_type}
                                    lorryCapacity={data.lorry_detail.capacity}
                                    lorrySize={data.lorry_detail.size}
                                    lorrySizeText={data.lorry_detail.size_text}
                                    lorryType={data.lorry_detail.type_value}
                                    lorryTyres={data.lorry_detail.total_tyres}
                                />
                            </div>
                        </div>
                        <div className={Styles.lorrySrcDestCol}>
                            <div className={Styles.srcDestContainer}>
                                <SrcDest
                                    cardCategory="lorry"
                                    source={data.lorry_detail.current_city}
                                    routes={data.lorry_detail.routes}
                                    position={isLastCard ? "top" : "bottom"}
                                />
                            </div>
                        </div>
                        <div className={Styles.lorryBadgeCol}>
                            <div className={Styles.statusContainer}>
                                <LorryBadgeController
                                    status={data.lorry_detail.status}
                                    verificationPending={isVerificationPending}
                                />

                                {data?.lorry_detail?.status === LORRY_STATUS.IN_TRANSIT && !isCsDashboardUser && (
                                    <div className={Styles.payBtnRedirection}>
                                        <AppRedirectPayBtn type="receive" fullWidth />
                                    </div>
                                )}

                                {data.lorry_detail.status !== LORRY_STATUS.IN_ACTIVE ? (
                                    <div className={Styles.bidStatusContainer}>
                                        <span className={classnames(Styles.bidStatus)}>{bidStatus}</span>
                                    </div>
                                ) : (
                                    <></>
                                )}
                            </div>
                        </div>
                        <div className={Styles.lorryPostedCol}>
                            {data.lorry_detail.status && data.lorry_detail.status !== LORRY_STATUS.IN_ACTIVE ? (
                                <div className={Styles.timeStamp}>
                                    <span>Posted at {data.lorry_detail.updated_at.split(",")[0]}</span>
                                    <span>{data.lorry_detail.updated_at.split(",")[1]}</span>
                                </div>
                            ) : (
                                <IfCanAccess
                                    allowCustomer
                                    action={ADMIN_OPERATION.userLoadLorryAction}
                                    alsoCheck={[LORRY_STATUS.IN_ACTIVE].includes(data.lorry_detail.status!)}
                                >
                                    <div className={Styles.reActivateCol}>
                                        <ReactivateLoadLorry
                                            type={"lorry"}
                                            data={data}
                                            onSuccess={handleReactivationSuccess}
                                            trackEvent={trackEvent}
                                            handleOpenEditModal={(category) => {
                                                handleEditClick(
                                                    getLorryPreFilledData(data),
                                                    data.lorry_detail.id,
                                                    category
                                                );
                                            }}
                                        />
                                    </div>
                                </IfCanAccess>
                            )}
                        </div>
                        <div className={Styles.editAndCommentContainer}>
                            <IfCanAccess action={ADMIN_OPERATION.userLoadLorryAction}>
                                <Col>{DropdownSlot}</Col>
                            </IfCanAccess>
                        </div>
                        <div className={classnames(Styles.arrowCol)}>{<ArrowIcon />}</div>
                    </Row>

                    <IfCanAccess action={""} allowCsUser alsoCheck={!!data?.lorry_detail?.meta_data?.body_type}>
                        <Row>
                            <Flex padding={"10px 50px 0"} gap={5}>
                                <TextLabelAndValue
                                    label="Verified Body"
                                    value={data?.lorry_detail?.meta_data?.body_type}
                                />
                            </Flex>
                        </Row>
                    </IfCanAccess>
                    <IfCanAccess action={ADMIN_OPERATION.userLoadLorryAction} allowCustomer>
                        {getRCVerificationComponent()}
                    </IfCanAccess>
                    {renderTapNgoTimeline && (
                        <Row>
                            <Flex padding={"10px 50px 0"} gap={5}>
                                {renderTapNgoTimeline}
                            </Flex>
                        </Row>
                    )}
                </div>
            ) : (
                // Mobile card
                <div
                    className={Styles.lorryCardContainer}
                    onClick={() => {
                        onCardClick?.();
                        onCardClickTrackingHandler
                            ? onCardClickTrackingHandler?.()
                            : trackEvent(LorryScreenEvents["lorry_card_clicked"]);
                    }}
                >
                    <div className={Styles.lorryCardSpaceWrapper}>
                        {VerificationStatus}
                        <div className={Styles.status}>
                            <LorryBadgeController
                                verificationPending={isVerificationPending}
                                status={data.lorry_detail.status}
                                outlined
                            />
                        </div>
                        {data.lorry_detail.expires_at && data.lorry_detail.status !== LORRY_STATUS.IN_ACTIVE ? (
                            <ExpiryPill expiry_time={data.lorry_detail.expires_at} expiresClass={Styles.expired} />
                        ) : (
                            <></>
                        )}
                        <Row>
                            <Col>
                                <div className={Styles.lorryClassificationContainer}>
                                    <DotSeparated
                                        dotStyleClassName={Styles.lorryId}
                                        nodes={[
                                            /* eslint-disable react/jsx-key */
                                            <span className={Styles.lorryId}>#LR{data.lorry_detail.id}</span>,
                                            <span className={Styles.postedAt}>
                                                Posted at{" "}
                                                {new Date(data.lorry_detail.created_at).toLocaleDateString("en-IN", {
                                                    year: "numeric",
                                                    month: "short",
                                                    day: "numeric"
                                                })}
                                                , {getFormattedTime(data.lorry_detail.created_at)}{" "}
                                            </span>
                                        ]}
                                    />
                                </div>
                            </Col>
                        </Row>
                        <Row>
                            <Col className={Styles.srcDestCol}>
                                <div className={Styles.srcDestContainer}>
                                    <SrcDest
                                        cardCategory="lorry"
                                        source={data.lorry_detail.current_city}
                                        routes={data.lorry_detail.routes}
                                    />
                                </div>
                            </Col>
                            <Col style={{ overflow: "hidden" }}>
                                <div className={Styles.icon}>
                                    <Flex flexDirection="column">
                                        <AppImg
                                            className={Styles.icon}
                                            src={getImageNameFromURL(lorry_icon_left)}
                                            alt=""
                                            width="100%"
                                            height="50px"
                                        />
                                        <div className={Styles.lorryNumber} style={{ paddingRight: 20 }}>
                                            <span>{formatLorryNum(data.lorry_detail.number)}</span>
                                        </div>
                                    </Flex>
                                </div>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <LorryOneLineInfo
                                    showLorryType
                                    lorryBodyType={data.lorry_detail.body_type}
                                    lorryCapacity={data.lorry_detail.capacity}
                                    lorrySize={data.lorry_detail.size}
                                    lorrySizeText={data.lorry_detail.size_text}
                                    lorryType={data.lorry_detail.type_value}
                                    lorryTyres={data.lorry_detail.total_tyres}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <div className={Styles.footerSectionWrapper}>
                                    {data.lorry_detail.status && data.lorry_detail.status !== LORRY_STATUS.IN_ACTIVE ? (
                                        <div className={Styles.footerSectionContainer}>
                                            {data?.lorry_detail?.status === LORRY_STATUS.IN_TRANSIT &&
                                            !isCsDashboardUser ? (
                                                <div style={{ width: "100%" }}>
                                                    <AppRedirectPayBtn type="receive" fullWidth />
                                                </div>
                                            ) : (
                                                <div className={Styles.bidStatusContainer}>
                                                    {/* The below code is used to display dot in mobile view based on status */}
                                                    {![
                                                        LORRY_STATUS.IN_TRANSIT,
                                                        LORRY_STATUS.VERIFICATION_FAILED
                                                    ].includes(data.lorry_detail.status) &&
                                                    !paymentPendingAssuredBids ? (
                                                        <span
                                                            className={classnames(
                                                                Styles.bidStatusIndicator,
                                                                Styles[
                                                                    sent_bids || received_bids
                                                                        ? "sentOrReceived"
                                                                        : "awaiting"
                                                                ]
                                                            )}
                                                        ></span>
                                                    ) : null}
                                                    <span className={classnames(Styles.bidStatus)}>{bidStatus}</span>
                                                </div>
                                            )}

                                            {data.lorry_detail.status === LORRY_STATUS.VERIFICATION_FAILED &&
                                            !isRCDocumentUploaded ? (
                                                <Flex margin={"0 10px"}>
                                                    <Typography weight="medium" color={COLORS.RED} size="s">
                                                        Please upload RC to verify your vehicle
                                                    </Typography>
                                                </Flex>
                                            ) : null}
                                        </div>
                                    ) : (
                                        <IfCanAccess
                                            allowCustomer
                                            action={ADMIN_OPERATION.userLoadLorryAction}
                                            alsoCheck={[LORRY_STATUS.IN_ACTIVE].includes(data.lorry_detail.status!)}
                                        >
                                            <div
                                                className={classnames(
                                                    Styles.footerSectionContainer,
                                                    Styles.reactivateContainer
                                                )}
                                            >
                                                <ReactivateLoadLorry
                                                    type={"lorry"}
                                                    data={data}
                                                    onSuccess={handleReactivationSuccess}
                                                    trackEvent={trackEvent}
                                                    handleOpenEditModal={(category) => {
                                                        handleEditClick(
                                                            getLorryPreFilledData(data),
                                                            data.lorry_detail.id,
                                                            category
                                                        );
                                                    }}
                                                />
                                            </div>
                                        </IfCanAccess>
                                    )}
                                </div>
                            </Col>
                        </Row>

                        {showProfileInfo ? (
                            <Row className={Styles.profileInfoWrapper}>
                                <Col>
                                    <Flex gap={5} justifyContent="space-between" onClick={(e) => e.stopPropagation()}>
                                        <div className={Styles.profileWrapper}>
                                            <AppImg
                                                src={getImageNameFromURL(data.company_detail.logo) || `default.png`}
                                                alt={""}
                                                width={40}
                                                height={30}
                                            />
                                            <Flex gap={12} alignItems="center">
                                                <Typography as="h1" size="sm">
                                                    <EllipsisText text={data.company_detail.name ?? ""} limit={15} />
                                                </Typography>
                                                <ProfileVerificationLevel
                                                    isBankVerified={data.company_detail.is_bank_verified ?? false}
                                                    isAadhaarVerified={data.company_detail.is_aadhaar_verified ?? false}
                                                    isGSTVerified={data.company_detail.is_gst_verified ?? false}
                                                    isMember={data.company_detail.is_member}
                                                />
                                            </Flex>
                                        </div>
                                        <div
                                            className={Styles.callBtn}
                                            onClick={() => onClickGetContact?.(data.company_detail.id)}
                                        >
                                            <CallCircle />
                                        </div>
                                    </Flex>
                                </Col>
                            </Row>
                        ) : (
                            <></>
                        )}
                        {renderTapNgoTimeline}
                    </div>
                    <IfCanAccess action={ADMIN_OPERATION.userLoadLorryAction} allowCustomer>
                        {getRCVerificationComponent()}
                    </IfCanAccess>
                </div>
            )}
            {activeCard === data.lorry_detail.id && openShareModal && (
                <ShareModal
                    toggle={() => {
                        setOpenShareModal(false);
                        setActiveCard(0);
                    }}
                    isOpen={!!openShareModal}
                    modalTitle={`${t("common:share_lorry")}`}
                    mktType={"lorry"}
                    data={data}
                    trackEvent={trackEvent}
                />
            )}
        </>
    );
};

export default LorryCard;

export const generateLorryEventAttribute = (lorry: LorryListTypes) => {
    const { lorry_detail } = lorry ?? {};
    const requiredLorry = getLorryDetailsByType(lorry_detail?.type_value);
    return {
        lorry_id: lorry_detail?.id,
        lorry_status: lorry_detail?.status_text,
        is_lorry_verified: lorry_detail?.is_verified,
        vehicle_number: lorry_detail?.number,
        current_location: lorry_detail?.current_city,
        current_location_id: lorry_detail?.current_location_id,
        current_location_city_id: lorry_detail?.source_info?.city_id,
        current_location_state_id: lorry_detail?.source_info?.state_id,
        routes: lorry_detail?.have_all_india_permit
            ? "All India Permit"
            : lorry_detail?.routes?.map((r) => STATE_LIST.find((st) => st.id === r?.id)?.code),
        total_route: lorry_detail?.routes?.length,
        lorry_type: requiredLorry?.label,
        lorry_capacity: lorry_detail?.capacity,
        capacity_type: requiredLorry?.unit,
        total_tyres: lorry_detail?.total_tyres,
        vehicle_size: lorry_detail?.size,
        vehicle_size_type: lorry_detail?.size_text,
        body_type: lorry_detail?.body_type != undefined ? LORRY_BODY_TYPE_MAP[lorry_detail?.body_type] : ""
    };
};

export const getLorryPreFilledData = (data: LorryListTypes): lorryCreationFormFieldsTypes => {
    const stateData = STATE_LIST.find((state) => state.name === data?.lorry_detail?.current_state);
    return {
        ...initialLoadCreationFormValues,
        lorryType: data?.lorry_detail?.type_value,
        isLorryCapacityRequired: true,
        lorryCapacity: data?.lorry_detail?.capacity,
        lorryBodyType: data?.lorry_detail?.body_type,
        lorrySize: data?.lorry_detail?.size,
        lorryTyres: data?.lorry_detail?.total_tyres,
        sizeText: data?.lorry_detail?.size_text,
        lorryNum: data?.lorry_detail?.number,
        routeIds: data?.lorry_detail?.routes,
        rcDocument: undefined,
        status: data?.lorry_detail?.status,
        currentCityText: getJoinedStringFromArray([
            removeSubstringWithParenthesis(data?.lorry_detail?.current_city?.split(",")[0] ?? ""),
            stateData?.name
        ]),
        currentCityOption: undefined,
        currentCityPlaceId: ""
    };
};
